/**
 * EntityNameWithCopy — Inspector-row label with hover-reveal Copy icon.
 *
 * Wherever the Node Inspector renders a counterparty as an entity name, a
 * truncated address, or both, hovering the name surface should reveal a small
 * Copy icon that puts the *underlying full wallet address* on the clipboard
 * (not the entity label). Investigators frequently need the raw address to
 * paste into Etherscan, a notice template, or a chat — they should never have
 * to expand a row to get it.
 *
 * Layout contract (see VISUAL_LANGUAGE_V2.md §3.4 Density rules):
 *   - The wrapper is `inline-flex` with a `min-w-0` text slot + a fixed-width
 *     icon slot. The icon is always present (occupying its slot); only its
 *     opacity flips on hover. This keeps row-to-row layout perfectly
 *     consistent — no flex-1 grid sibling shrinks or grows when rows lack a
 *     hover state.
 *   - Uses Tailwind's named-group syntax (`group/copy`) so we don't collide
 *     with any outer `.group` declared by the parent row (Step 8 will add
 *     hover action buttons at the row level — both groups must coexist).
 *   - Click handler stops propagation so copying doesn't double-fire row click.
 */
import React from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import clsx from 'clsx';

interface Props {
    /** The full underlying wallet address to copy. May be empty — copy is a no-op then. */
    address: string;
    /** What to render in the label slot (entity name, truncated address, mixed). */
    children: React.ReactNode;
    /** Class on the outer wrapper. Apply your colour / weight rules here. */
    className?: string;
    /** Optional tooltip on the wrapper (typically the full address). */
    title?: string;
    /**
     * Default `truncate` will be applied to the text slot. Pass `false` to opt
     * out (e.g. when caller controls overflow themselves).
     */
    truncate?: boolean;
    /** Tailwind sizing for the Copy icon. Defaults to `w-3 h-3`. */
    iconClassName?: string;
}

export const EntityNameWithCopy: React.FC<Props> = ({
    address,
    children,
    className,
    title,
    truncate = true,
    iconClassName = 'w-3 h-3',
}) => {
    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!address) return;
        navigator.clipboard.writeText(address);
        toast.success('Copied address');
    };

    return (
        <span
            className={clsx('group/copy inline-flex items-center gap-1 min-w-0', className)}
            title={title}
        >
            <span className={clsx(truncate && 'truncate', 'min-w-0 flex-1')}>{children}</span>
            <Copy
                onClick={handleCopy}
                className={clsx(
                    iconClassName,
                    'shrink-0 cursor-pointer opacity-0 group-hover/copy:opacity-100 transition-opacity text-muted-foreground hover:text-foreground'
                )}
                aria-label="Copy address"
            />
        </span>
    );
};

export default EntityNameWithCopy;
